/**
 * Calendario con datos reales (solo lectura).
 *
 * Lee GET /jobs/events/self de hoy y los próximos 6 días (días civiles de la empresa) y lo
 * convierte al modelo del calendario: un carril por trabajador (todavía no existe API de “equipos”).
 * Reglas: no escribe nada; la llegada solo se marca con startAt (registro de inicio);
 * las visitas sin trabajador van a “Sin asignar”.
 */
import { DateTime } from 'luxon';
import { gateway } from '../../../gateway';
import type { Snapshot } from '../lib/scheduling';
import type { Assignment, AssignmentStatus, Block, Client, GeoPoint, Team, Visit, VisitStatus } from './types';
import { addDays } from '../lib/time';

const COLORS = ['#2F6FDE', '#8E44AD', '#0F8C7B', '#C2255C', '#E36414', '#5C7C1A', '#3A3A3D', '#0E7490'];
/** Jornada usada para calcular ocupación y espacios libres hasta que exista API de capacidad. */
export const DEFAULT_SHIFT = { start: 8, end: 18 };

/** Forma mínima que usamos de cada evento de /jobs/events/self. */
export interface RawEvent {
  uid: string;
  startDate: string;
  endDate: string;
  status?: string | null;
  updatedAt?: string;
  jobId?: number;
  job?: { id?: number; alias?: string | null; status?: string; client?: RawPerson; coverageArea?: RawArea } | null;
  client?: RawPerson | null;
  coverageArea?: RawArea | null;
  workforce?: RawWorker[] | null;
}
interface RawPerson { uid?: string; firstName?: string | null; lastName?: string | null; alias?: string | null }
interface RawArea { address?: string | null; city?: string | null; latitude?: string | number | null; longitude?: string | number | null }
interface RawWorker extends RawPerson {
  status?: string | null;
  startAt?: string | null;
  endAt?: string | null;
  WorkforcesByJobEvents?: { status?: string | null; startAt?: string | null; endAt?: string | null; workforceUid?: string | null } | null;
}

export interface RealDay {
  /** `today` es hoy en la zona de la empresa; las visitas llevan su propia fecha. */
  snapshot: Snapshot;
  /** Visitas sin nadie asignado (todas las fechas del rango). */
  unassigned: Visit[];
  /** Visitas que empiezan o terminan fuera de 8:00–18:00 (se recortan en la vista). */
  outsideShift: number;
  /** Visitas reales del rango (sin contar canceladas). */
  total: number;
  /** Días que cubre la lectura, empezando por hoy. */
  days: number;
  /** El servidor reportó más visitas de las recibidas (lectura incompleta). */
  partial?: boolean;
}

/** Días que se leen de una vez: hoy + 6. */
export const RANGE_DAYS = 7;

const VISIT_STATUS: Record<string, VisitStatus> = {
  scheduled: 'scheduled', approved: 'scheduled', forRevision: 'scheduled', support: 'scheduled',
  inProgress: 'inProgress', completed: 'completed', archived: 'completed', cancelled: 'cancelled',
};
const ASSIGN_STATUS: Record<string, AssignmentStatus> = {
  pending: 'assigned', assigned: 'assigned', approved: 'assigned',
  inProgress: 'inProgress', completed: 'completed', declined: 'declined', canceled: 'declined',
};

const fullName = (p?: RawPerson | null) =>
  [p?.firstName, p?.lastName].filter(Boolean).join(' ').trim() || p?.alias?.trim() || '';

const initials = (name: string) =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]!.toUpperCase()).join('') || '?';

/** Hora decimal en la zona de la empresa, relativa al inicio de `day` (puede salir de 0–24 si cruza días). */
function hourIn(iso: string, zone: string, day: DateTime): number {
  const t = DateTime.fromISO(iso, { zone: 'utc' }).setZone(zone);
  return Math.round(t.diff(day, 'minutes').minutes) / 60;
}

export function toRealDay(items: RawEvent[], zone: string, now: DateTime, days = 1): RealDay {
  const local = now.setZone(zone);
  const today = local.startOf('day').toISODate()!;
  const teams = new Map<string, Team>();
  const clients = new Map<string, Client>();
  const visits: Visit[] = [];
  const unassigned: Visit[] = [];
  let outsideShift = 0;
  let total = 0;

  for (const ev of items) {
    if (!ev?.uid || !ev.startDate || !ev.endDate) continue;
    const status = VISIT_STATUS[ev.status ?? ev.job?.status ?? ''] ?? 'scheduled';
    if (status === 'cancelled') continue;
    total += 1;
    // Cada visita se ubica en el día civil (zona de la empresa) en que empieza.
    const day = DateTime.fromISO(ev.startDate, { zone: 'utc' }).setZone(zone).startOf('day');
    const date = day.toISODate()!;
    const start = Math.max(0, hourIn(ev.startDate, zone, day));
    const end = Math.min(24, hourIn(ev.endDate, zone, day));
    if (start < DEFAULT_SHIFT.start || end > DEFAULT_SHIFT.end) outsideShift += 1;

    const person = ev.client ?? ev.job?.client ?? null;
    const area = ev.coverageArea ?? ev.job?.coverageArea ?? null;
    const clientUid = person?.uid ?? `client-${ev.uid}`;
    if (!clients.has(clientUid)) {
      clients.set(clientUid, {
        uid: clientUid,
        name: fullName(person) || ev.job?.alias || 'Cliente',
        kind: 'cliente',
        address: [area?.address, area?.city].filter(Boolean).join(', '),
        location: { lat: Number(area?.latitude) || 0, lng: Number(area?.longitude) || 0 },
        flexible: false,
      });
    }

    const workers = (ev.workforce ?? []).filter(Boolean);
    const assignments: Assignment[] = workers.map(w => {
      const link = w.WorkforcesByJobEvents ?? {};
      const startAt = link.startAt ?? w.startAt;
      const endAt = link.endAt ?? w.endAt;
      return {
        memberUid: link.workforceUid ?? w.uid ?? fullName(w),
        status: ASSIGN_STATUS[link.status ?? w.status ?? ''] ?? 'assigned',
        startAt: startAt ? hourIn(startAt, zone, day) : undefined,
        endAt: endAt ? hourIn(endAt, zone, day) : undefined,
      };
    });
    const jobId = ev.job?.id ?? ev.jobId;
    const base: Omit<Visit, 'uid' | 'teamId'> = {
      date, clientUid, service: 'regular', start, end, status, assignments,
      updatedAt: ev.updatedAt ?? '',
      ...(typeof jobId === 'number' ? { ref: { jobId, eventUid: ev.uid } } : {}),
    };

    const active = workers.filter((_, i) => assignments[i]!.status !== 'declined');
    if (!active.length) {
      unassigned.push({ ...base, uid: ev.uid, teamId: '' });
      continue;
    }
    for (const w of active) {
      const id = w.WorkforcesByJobEvents?.workforceUid ?? w.uid ?? fullName(w);
      if (!teams.has(id)) {
        const name = fullName(w) || 'Trabajador';
        teams.set(id, {
          id, name, color: '',
          members: [{ uid: id, name, initials: initials(name), locationConsent: false }],
          workStart: DEFAULT_SHIFT.start, workEnd: DEFAULT_SHIFT.end, base: { lat: 0, lng: 0 },
        });
      }
      visits.push({ ...base, uid: `${ev.uid}:${id}`, teamId: id });
    }
  }

  const sorted = [...teams.values()].sort((a, b) => a.name.localeCompare(b.name));
  sorted.forEach((t, i) => { t.color = COLORS[i % COLORS.length]!; });
  return {
    snapshot: {
      today, now: local.hour + local.minute / 60,
      teams: sorted, visits, blocks: [], clients: [...clients.values()], closedDays: [],
    },
    unassigned, outsideShift, total, days,
  };
}

/** Solo lectura. Lee de las 0:00 de hoy al último milisegundo del día 7 (zona de la empresa);
 * el backend trata endDate como inclusivo, igual que countRealVisits. */
export async function fetchRealDay(zone: string, now = DateTime.now(), days = RANGE_DAYS): Promise<RealDay> {
  const start = now.setZone(zone).startOf('day');
  if (!start.isValid || !zone) throw new Error('Zona horaria de empresa no disponible');
  const response = await gateway.get('/jobs/events/self', {
    params: {
      startDate: start.toUTC().toISO(),
      endDate: start.plus({ days }).minus({ milliseconds: 1 }).toUTC().toISO(),
      page: 1, pageSize: 2000,
    },
  });
  const items = response.data?.data?.items;
  if (!Array.isArray(items)) throw new Error('Respuesta de visitas inválida');
  const day = toRealDay(items as RawEvent[], zone, now, days);
  const reported = response.data?.data?.totalItems;
  return typeof reported === 'number' && reported > items.length ? { ...day, partial: true } : day;
}

/** Punto neutro: sin geocodificar la dirección del cliente nuevo, la distancia no cuenta. */
export const NO_LOCATION: GeoPoint = { lat: 0, lng: 0 };

/**
 * Snapshot para “¿Hay espacio?” con datos reales (solo para buscar huecos; no se muestra):
 *  - Un trabajador solo cuenta como disponible los días en que ya tiene alguna visita
 *    (sin API de turnos no sabemos quién descansa; así no ofrecemos a alguien que no trabaja).
 *  - Fuera del rango leído no hay datos: esos días se tratan como cerrados.
 *  - Distancia neutralizada y ningún cliente “flexible” (el paso 2 necesita esa API).
 */
export function availabilitySnapshot(day: RealDay): Snapshot {
  const s = day.snapshot;
  const dates = Array.from({ length: day.days }, (_, i) => addDays(s.today, i));
  const off: Block[] = [];
  for (const t of s.teams) {
    for (const d of dates) {
      if (!s.visits.some(v => v.teamId === t.id && v.date === d)) {
        off.push({ teamId: t.id, date: d, start: t.workStart, end: t.workEnd, label: 'Sin visitas ese día' });
      }
    }
  }
  return {
    ...s,
    teams: s.teams.map(t => ({ ...t, base: NO_LOCATION })),
    clients: s.clients.map(c => ({ ...c, location: NO_LOCATION, flexible: false })),
    blocks: [...s.blocks, ...off],
    closedDays: [...s.closedDays, ...[0, 1, 2].map(i => addDays(s.today, day.days + i))],
  };
}
